import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaFilter, FaPaperPlane, FaPen, FaPlus, FaSearch, FaSlidersH, FaTimes } from 'react-icons/fa';
import ErpTemplate from '../../components/ErpTemplate';
import { FinField } from '../../components/FinField';
import ReactSelect from '../../components/ReactSelect';
import api from '../../services/api';
import { TIPOS_EVENTO, rotuloCliente } from './eventoTipos';
import '../../css/financeiro-parcela-pagar.css';

const CATEGORIAS = {
    cliente: 'Cliente',
    access: 'Access',
    financeiro: 'Financeiro',
};

const OPCOES_CATEGORIA = [
    { value: 'cliente', label: 'Cliente' },
    { value: 'access', label: 'Access' },
    { value: 'financeiro', label: 'Financeiro' },
];

const OPCOES_BACKOFFICE = [
    { value: 'todos', label: 'Todos' },
    { value: 'terceiro', label: 'Terceiro' },
    { value: 'exclusivo', label: 'Exclusivo' },
];

const OPCOES_COMPLEXIDADE = [
    { value: 'todos', label: 'Todas' },
    { value: 'baixa', label: 'Baixa' },
    { value: 'media', label: 'Média' },
    { value: 'alta', label: 'Alta' },
];

const OPCOES_SITUACAO = [
    { value: '1', label: 'Ativo' },
    { value: '0', label: 'Inativo' },
];

// Só a descrição fica na busca rápida; o resto vive no modal. Manter as duas listas separadas é o
// que permite contar quantos filtros avançados estão ligados sem contar a busca.
const FILTRO_VAZIO = {
    cliente: null,
    nomeCliente: '',
    categoria: '',
    backoffice: '',
    tipo: '',
    complexidade: '',
    ativo: '',
};

export default function PainelSla() {
    const token = localStorage.getItem('token');
    const perfil = localStorage.getItem('perfil');
    const [filtro, setFiltro] = useState({ descricao: '', buscando: false, ...FILTRO_VAZIO });
    const [slas, setSlas] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const [mostrarFiltros, setMostrarFiltros] = useState(false);
    const controllerRef = useRef(null);

    // Quantos filtros avançados estão ligados — vira o badge no botão, para o operador saber que a
    // lista está recortada mesmo com o modal fechado.
    const filtrosAtivos = Object.keys(FILTRO_VAZIO)
        .filter((chave) => chave !== 'nomeCliente')
        .filter((chave) => filtro[chave] !== '' && filtro[chave] !== null && filtro[chave] !== undefined)
        .length;

    // Critérios expõem colunas do banco — só admin. Diretor administra SLA, mas não o catálogo.
    const podeVerCriterios = perfil === 'admin';

    const [disparando, setDisparando] = useState(false);

    // Provisório: existe só para testar os alertas enquanto o agendamento não está configurado.
    // Bate no endpoint autenticado (não no de token, que é da máquina e não pode vir do front).
    const dispararAlertas = () => {
        if (disparando) return;
        setDisparando(true);
        api({ method: 'post', action: '/sla/disparar', token })
            .then((response) => {
                toast.success(response.data.data?.mensagem ?? 'Varredura executada.');
            })
            .catch((error) => {
                toast.error('Erro ao disparar: ' + (error.response?.data?.error ?? error.message ?? ''));
            })
            .finally(() => setDisparando(false));
    };

    // `override` existe para quem muda o filtro e busca no MESMO clique (o "Limpar filtros"):
    // o setFiltro é assíncrono, então sem ele a busca sairia com o filtro anterior.
    const buscar = (override) => {
        const atual = override ? { ...filtro, ...override } : filtro;

        if (controllerRef.current) controllerRef.current.abort();
        controllerRef.current = new AbortController();
        setFiltro((state) => ({ ...state, buscando: true }));
        api({
            method: 'get',
            action: '/sla',
            token,
            params: {
                descricao: atual.descricao,
                cliente: atual.cliente ?? '',
                categoria: atual.categoria,
                backoffice: atual.backoffice,
                tipo: atual.tipo,
                complexidade: atual.complexidade,
                ativo: atual.ativo,
            },
            signal: controllerRef.current.signal
        })
            .then((response) => {
                setSlas(response.data.data ?? []);
                setPage(1);
                setFiltro((state) => ({ ...state, buscando: false }));
            })
            .catch((err) => {
                // Cancelamento é o abort da busca anterior, não erro.
                if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') return;
                setFiltro((state) => ({ ...state, buscando: false }));
                // Sem isto, 403 ou 500 renderizam "Nenhum registro" — indistinguível de base
                // vazia, e o operador conclui que os cadastros sumiram.
                toast.error('Erro ao buscar: ' + (err.response?.data?.error ?? err.message ?? ''));
            });
    };

    useEffect(() => {
        document.querySelector('#descricao')?.focus();
        buscar();
        return () => { if (controllerRef.current) controllerRef.current.abort(); };
        // eslint-disable-next-line
    }, []);

    const paginados = useMemo(() => {
        const inicio = (page - 1) * pageSize;
        return slas.slice(inicio, inicio + pageSize);
    }, [slas, page, pageSize]);

    const totalPaginas = Math.max(1, Math.ceil(slas.length / pageSize));

    // 'todos' marca 'vale para qualquer valor' — na tela vira um traço, que lê melhor que a palavra.
    const abrangencia = (valor) => (valor === 'todos' || !valor ? '—' : valor);

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head">
                    <div>
                        <h1 className="fin-pp__title">SLA</h1>
                        <p className="fin-pp__subtitle">Regras de SLA por cliente e categoria, com seus alertas.</p>
                    </div>
                    <div className="fin-pp__actions">
                        {/* Provisório: dispara a varredura na hora, enquanto o agendamento
                            automático não existe. Só admin, e clicar duas vezes é inofensivo —
                            o unique por dia do sla_envio impede o segundo envio. */}
                        {podeVerCriterios && (
                            <button
                                type="button"
                                className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm"
                                title="Executa a varredura de alertas agora — somente admin"
                                disabled={disparando}
                                onClick={dispararAlertas}
                            >
                                <FaPaperPlane size={14} /> {disparando ? 'Disparando...' : 'Disparar alertas'}
                                <span className="fin-pp__pill is-warn" style={{ marginLeft: 6 }}>admin</span>
                            </button>
                        )}
                        {/* Só admin: o catálogo de critérios lista colunas do banco. Diretor
                            administra SLA e alertas, mas não vê esta porta. */}
                        {podeVerCriterios && (
                            <Link
                                to="/slaCriterio"
                                className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm"
                                title="Catálogo de critérios — somente admin"
                            >
                                <FaSlidersH size={14} /> Critérios
                                <span className="fin-pp__pill is-warn" style={{ marginLeft: 6 }}>admin</span>
                            </Link>
                        )}
                        <Link to="/cadastroSla" className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--pill">
                            <FaPlus size={14} /> Novo SLA
                        </Link>
                    </div>
                </div>

                <form
                    className="fin-pp__card fin-pp__search"
                    style={{ marginBottom: 16, padding: '12px 16px' }}
                    onSubmit={(e) => { buscar(); e.preventDefault(); }}
                >
                    <div className="fin-pp__search-field">
                        <FaSearch className="fin-pp__search-ic" size={16} />
                        <input
                            id="descricao"
                            className="fin-pp__input"
                            type="text"
                            placeholder="Buscar pela descrição do SLA"
                            value={filtro.descricao ?? ''}
                            onChange={(e) => setFiltro((state) => ({ ...state, descricao: e.target.value }))}
                        />
                    </div>
                    <button type="submit" className="fin-pp__btn fin-pp__btn--primary" disabled={filtro.buscando}>
                        {filtro.buscando ? 'Buscando...' : 'Buscar'}
                    </button>
                    <button type="button" className="fin-pp__btn" onClick={() => setMostrarFiltros(true)}>
                        <FaFilter size={14} /> Filtros
                        {filtrosAtivos > 0 && (
                            <span className="fin-pp__pill is-info" style={{ marginLeft: 6 }}>
                                {filtrosAtivos}
                            </span>
                        )}
                    </button>
                </form>

                {/* Renderizado só quando aberto, e não escondido por display: o select de Cliente
                    aqui dentro é um AsyncSelect com defaultOptions, então a versão sempre montada
                    disparava um GET /participante a cada carga do painel, inclusive para quem
                    nunca abre os filtros. */}
                {mostrarFiltros && (
                <div
                    className="modal fade show d-block fin-pp fin-pp__modalbg"
                    tabIndex="-1"
                    onClick={(e) => { if (e.target === e.currentTarget) setMostrarFiltros(false); }}
                >
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content fin-pp__modal">
                            <div className="fin-pp__modal-head">
                                <span className="fin-pp__modal-head-ic"><FaFilter size={17} /></span>
                                <h3 className="fin-pp__modal-title">Filtros
                                    <span className="fin-pp__modal-sub">Refine as regras de SLA listadas</span>
                                </h3>
                                <button type="button" className="fin-pp__modal-close" onClick={() => setMostrarFiltros(false)}>
                                    <FaTimes size={16} />
                                </button>
                            </div>
                            <div className="modal-body">
                                <div className="fin-pp__filters">
                                    <FinField label="Cliente" grow>
                                        <ReactSelect
                                            isClearable
                                            value={filtro.cliente ? { value: filtro.cliente, label: filtro.nomeCliente } : null}
                                            onChange={(e) => setFiltro((state) => ({ ...state, cliente: e?.value ?? null, nomeCliente: e?.label ?? '' }))}
                                            loadOptions={(input) => api({
                                                method: 'get',
                                                action: '/participante',
                                                token,
                                                params: { nome: input, tipoCadastro: 'cliente' }
                                            }).then((response) => (response.data.data ?? []).map((registro) => ({
                                                value: registro.id,
                                                label: rotuloCliente(registro)
                                            }))).catch(() => [])}
                                        />
                                    </FinField>

                                    <FinField label="Categoria">
                                        <ReactSelect
                                            isClearable
                                            value={OPCOES_CATEGORIA.find((item) => item.value === filtro.categoria) ?? null}
                                            onChange={(e) => setFiltro((state) => ({ ...state, categoria: e?.value ?? '' }))}
                                            options={OPCOES_CATEGORIA}
                                        />
                                    </FinField>

                                    <FinField label="Backoffice">
                                        <ReactSelect
                                            isClearable
                                            value={OPCOES_BACKOFFICE.find((item) => item.value === filtro.backoffice) ?? null}
                                            onChange={(e) => setFiltro((state) => ({ ...state, backoffice: e?.value ?? '' }))}
                                            options={OPCOES_BACKOFFICE}
                                        />
                                    </FinField>

                                    <FinField label="Tipo de evento">
                                        <ReactSelect
                                            isClearable
                                            value={TIPOS_EVENTO.find((item) => item.value === filtro.tipo) ?? null}
                                            onChange={(e) => setFiltro((state) => ({ ...state, tipo: e?.value ?? '' }))}
                                            options={TIPOS_EVENTO}
                                        />
                                    </FinField>

                                    <FinField label="Complexidade">
                                        <ReactSelect
                                            isClearable
                                            value={OPCOES_COMPLEXIDADE.find((item) => item.value === filtro.complexidade) ?? null}
                                            onChange={(e) => setFiltro((state) => ({ ...state, complexidade: e?.value ?? '' }))}
                                            options={OPCOES_COMPLEXIDADE}
                                        />
                                    </FinField>

                                    <FinField label="Situação">
                                        <ReactSelect
                                            isClearable
                                            value={OPCOES_SITUACAO.find((item) => item.value === filtro.ativo) ?? null}
                                            onChange={(e) => setFiltro((state) => ({ ...state, ativo: e?.value ?? '' }))}
                                            options={OPCOES_SITUACAO}
                                        />
                                    </FinField>
                                </div>
                            </div>
                            <div className="fin-pp__modal-foot">
                                <button
                                    type="button"
                                    className="fin-pp__btn"
                                    // Limpa só os avançados: a busca por descrição fica, senão o
                                    // operador perderia o que digitou fora do modal. Rebusca junto,
                                    // senão o contador zera enquanto a tabela segue filtrada.
                                    onClick={() => {
                                        setFiltro((state) => ({ ...state, ...FILTRO_VAZIO }));
                                        buscar(FILTRO_VAZIO);
                                    }}
                                >
                                    Limpar filtros
                                </button>
                                <button
                                    type="button"
                                    className="fin-pp__btn fin-pp__btn--primary"
                                    onClick={() => { setMostrarFiltros(false); buscar(); }}
                                >
                                    <FaSearch size={14} /> Aplicar filtros
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                )}

                <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                    <div className="fin-pp__tblwrap">
                        <table className="fin-pp__tbl" style={{ minWidth: 1040, tableLayout: 'auto' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 62 }}></th>
                                    <th style={{ width: 100 }}>Situação</th>
                                    <th>Descrição</th>
                                    <th>Cliente</th>
                                    <th style={{ width: 110 }}>Categoria</th>
                                    <th style={{ width: 110 }}>Backoffice</th>
                                    <th style={{ width: 120 }}>Tipo</th>
                                    <th style={{ width: 120 }}>Complexidade</th>
                                    <th style={{ width: 90 }}>Alertas</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtro.buscando ? (
                                    <tr><td colSpan={9} className="fin-pp__empty"><span className="fin-pp__loading"><span className="fin-pp__spin"></span>Buscando...</span></td></tr>
                                ) : paginados.length === 0 ? (
                                    <tr><td colSpan={9} className="fin-pp__empty">Nenhum registro</td></tr>
                                ) : paginados.map((row) => (
                                    <tr key={row.id}>
                                        <td>
                                            <Link to={`/cadastroSla/${row.id}`} className="fin-pp__rowbtn" title="Alterar SLA">
                                                <FaPen size={14} />
                                            </Link>
                                        </td>
                                        <td>
                                            <span className={`fin-pp__pill ${row.ativo ? 'is-ok' : 'is-muted'}`}>
                                                {row.ativo ? 'ativo' : 'inativo'}
                                            </span>
                                        </td>
                                        <td className="fin-pp__cell-strong">{row.descricao ?? ''}</td>
                                        <td className="fin-pp__cell-muted">{row.cliente?.nome ?? ''}</td>
                                        <td className="fin-pp__cell-muted">{CATEGORIAS[row.categoria] ?? row.categoria}</td>
                                        <td className="fin-pp__cell-muted">{abrangencia(row.backoffice)}</td>
                                        <td className="fin-pp__cell-muted">{abrangencia(row.tipo)}</td>
                                        <td className="fin-pp__cell-muted">{abrangencia(row.complexidade)}</td>
                                        <td className="fin-pp__cell-muted">{(row.alertas ?? []).length}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="fin-pp__pagination">
                        <div className="fin-pp__pagination-info">{slas.length} registros</div>
                        <div className="fin-pp__pagination-ctrls">
                            <select
                                className="fin-pp__select"
                                style={{ height: 34 }}
                                value={pageSize}
                                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                            >
                                <option value={15}>15 / pág</option>
                                <option value={30}>30 / pág</option>
                                <option value={50}>50 / pág</option>
                            </select>
                            <button type="button" className="fin-pp__btn fin-pp__btn--sm" disabled={page <= 1} onClick={() => setPage((state) => state - 1)}>
                                Anterior
                            </button>
                            <span className="fin-pp__pagination-info">{page} / {totalPaginas}</span>
                            <button type="button" className="fin-pp__btn fin-pp__btn--sm" disabled={page >= totalPaginas} onClick={() => setPage((state) => state + 1)}>
                                Próxima
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </ErpTemplate>
    );
}
